import { useState } from "react";

import useVehiclesMutations from "../../../data/useVehiclesMutations";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import { IPart } from "../../../models/Part";
import classNames from "../../../utils/classNames";
import KTypeLookupModal from "../../shared/KTypeLookupModal";

export default function UnlistedKTypeUpdate({ part }: { part: IPart }) {
  const { update } = useVehiclesMutations();
  const { saveHandlers } = useDefaultCRUDHandlers("KType");
  const [lookupOpen, setLookupOpen] = useState(false);

  const saveKType = (kType: number | null) => {
    if (!part.vehicle) return;
    if (part.vehicle.kType === kType) return;

    const updateVehicle = {
      ...part.vehicle,
      kType,
    };

    update.mutate(updateVehicle, {
      ...saveHandlers,
      onSuccess: () => {
        saveHandlers.onSuccess();
        if (part.vehicle) {
          part.vehicle.kType = kType;
        }
      },
    });
  };

  const handleKTypeUpdate = (kTypeString: string) => {
    if (!part.vehicle) return;

    let kType: number | null = Number.parseInt(kTypeString, 10);

    if (Number.isNaN(kType)) {
      kType = null;
    }

    saveKType(kType);
  };

  const inputClass = classNames(
    "w-full min-w-0 rounded border border-slate-200 bg-white px-1.5 py-0.5 text-xs text-black",
    "focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
  );

  return (
    <>
      <KTypeLookupModal
        open={lookupOpen}
        onClose={() => setLookupOpen(false)}
        initialMake={part.vehicle?.combinedMake}
        initialModel={part.vehicle?.modelRangeDescription || part.vehicle?.combinedModel}
        vehicle={part.vehicle}
        onSelect={(kType) => {
          saveKType(kType);
          setLookupOpen(false);
        }}
      />

      <div
        className="flex items-center gap-1.5 text-xs text-black"
        onClick={(e) => e.stopPropagation()}
      >
        <label htmlFor={`kType-${part.id}`} className="flex min-w-0 flex-1 items-center gap-1.5">
          <span className="w-10 flex-none font-semibold uppercase tracking-wide text-black">
            KType
          </span>
          <input
            id={`kType-${part.id}`}
            key={`${part.id}-${part.vehicle?.kType ?? ""}`}
            defaultValue={part.vehicle?.kType ?? ""}
            className={inputClass}
            type="number"
            onBlur={(e) => handleKTypeUpdate(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                (e.target as HTMLInputElement).blur();
              }
            }}
          />
        </label>
        <button
          type="button"
          onClick={() => setLookupOpen(true)}
          className="shrink-0 rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-700 hover:bg-slate-50"
          title="Lookup KType from Amazon MVL"
        >
          Lookup
        </button>
      </div>
    </>
  );
}
